import React, { useEffect, useRef, useState } from "react";
import { Images } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

// Admin › Intégrations : variantes allégées (128/480/1280 px) pour les photos envoyées AVANT le lot 380
// + dimensions des vidéos des anciens posts (le fil réserve la bonne hauteur).
// Lot 679 — 2e passe : tailles 864 / 1080 (photo seule du fil sur téléphone, 2 à 3× moins de pixels que 1280) ajoutées aux
// photos existantes ; une fois TOUT généré sans échec, le serveur publie ces tailles (settings.image_variant_widths) et les
// appareils les demandent sans mise à jour. L'interrupteur « Servir 864/1080 » permet de revenir à 128/480/1280 à tout moment.
const WIDTHS_V1 = [128, 480, 1280];
const WIDTHS_V2 = [128, 480, 864, 1080, 1280];
export const ImageVariantsCard = ({ canManage = true }) => {
  const [st, setSt] = useState(null);
  const pollRef = useRef(null);
  const fetchStatus = async () => {
    try { const { data } = await api.get("/admin/integrations/storage/image-variants/status"); setSt(data); return data; } catch { return null; }
  };
  useEffect(() => { fetchStatus(); return () => clearInterval(pollRef.current); }, []);

  const start = async () => {
    try {
      await api.post("/admin/integrations/storage/image-variants");
      clearInterval(pollRef.current);
      const tick = async () => {
        const s = await fetchStatus();
        if (s && !s.running) {
          clearInterval(pollRef.current); pollRef.current = null;
          if ((s.failed || 0) === 0) toast.success(`Variantes générées ✓ (${s.done} ancienne(s) photo(s), ${s.done_v2 || 0} photo(s) complétée(s) en 864/1080, ${s.videos} vidéo(s) mesurée(s))`);
          else toast.warning(`Terminé avec ${s.failed} échec(s) — relance possible`);
        }
      };
      pollRef.current = setInterval(tick, 2000);
      tick();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };
  const toggleLegacy = async (v) => {
    try {
      await api.patch("/admin/settings", { image_variants_legacy: v });
      setSt((s) => ({ ...(s || {}), legacy_enabled: v }));
      toast.success(v ? "Les anciennes photos sont servies en variantes légères" : "Anciennes photos : original seulement");
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };
  // Lot 679 — publier / retirer les tailles 864/1080 (retrait = retour immédiat à 128/480/1280 sur tous les appareils).
  const toggleWidthsV2 = async (v) => {
    try {
      await api.patch("/admin/settings", { image_variant_widths: v ? WIDTHS_V2 : WIDTHS_V1 });
      setSt((s) => ({ ...(s || {}), widths_v2_enabled: v, widths: v ? WIDTHS_V2 : WIDTHS_V1 }));
      toast.success(v ? "Tailles 864 / 1080 servies dans le fil" : "Retour aux tailles 128 / 480 / 1280");
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  const processed = st ? (st.done || 0) + (st.done_v2 || 0) + (st.skipped || 0) + (st.failed || 0) : 0;
  const remainingAll = st && st.remaining != null ? (st.remaining || 0) + (st.remaining_v2 || 0) : null;
  return (
    <div className="yalola-card p-5 space-y-3" data-testid="int-image-variants-card">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}><Images size={18} /></div>
        <div className="font-bold font-display text-lg">Photos — variantes légères</div>
        {st && !st.running && remainingAll != null && (
          <span className={`chip text-[10px] ml-auto ${remainingAll === 0 ? "chip-primary" : ""}`} data-testid="int-image-variants-remaining">
            {remainingAll === 0 ? "Tout est généré" : `${remainingAll} photo(s) à traiter`}
          </span>
        )}
      </div>
      <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
        Chaque photo existe en 5 tailles (128 / 480 / 864 / 1080 / 1280 px) ; le fil charge la plus petite qui reste nette pour l'écran. Ce traitement crée les tailles manquantes
        des photos plus anciennes ({st?.remaining ?? "…"} sans aucune variante, {st?.remaining_v2 ?? "…"} sans 864 / 1080) et mesure les vidéos des anciens posts. Il tourne en arrière-plan ; relançable sans risque.
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" className="rounded-full" onClick={start} disabled={!canManage || !!st?.running || remainingAll === 0} data-testid="int-image-variants-run">
          {st?.running ? "Génération en cours..." : "Générer les variantes"}
        </Button>
        {st && (st.running || st.finished_at) && (
          <span className="text-xs font-medium" data-testid="int-image-variants-status">
            {st.running
              ? `En cours : ${processed} / ${st.total} photos...`
              : `Terminé : ${st.done} ancienne(s) · ${st.done_v2 || 0} complétée(s) 864/1080 · ${st.skipped || 0} écartée(s) · ${st.failed} échec(s) · ${st.videos} vidéo(s) mesurée(s)`}
          </span>
        )}
      </div>
      {st?.running && st.total > 0 && (
        <div className="h-1.5 rounded-full bg-black/10 overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${Math.round((processed / st.total) * 100)}%`, background: "var(--yalola-primary)" }} />
        </div>
      )}
      <div className="flex items-center justify-between rounded-xl px-4 py-2 gap-3" style={{ background: "var(--yalola-surface-alt)" }}>
        <div className="text-sm">
          <div>Servir les anciennes photos en variantes légères</div>
          <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>Activé automatiquement quand la génération se termine sans échec. Ne l'active pas à la main avant : une variante absente = image cassée.</div>
        </div>
        <Switch checked={!!st?.legacy_enabled} disabled={!canManage} onCheckedChange={toggleLegacy} data-testid="int-image-variants-legacy" />
      </div>
      <div className="flex items-center justify-between rounded-xl px-4 py-2 gap-3" style={{ background: "var(--yalola-surface-alt)" }}>
        <div className="text-sm">
          <div>Servir les tailles 864 / 1080 dans le fil (téléphone : 2 à 3× moins de mémoire et de données par photo)</div>
          <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>Activé automatiquement quand la génération se termine sans échec. Désactiver = retour immédiat à 128 / 480 / 1280 sur tous les appareils, sans mise à jour.</div>
        </div>
        <Switch checked={!!st?.widths_v2_enabled} disabled={!canManage} onCheckedChange={toggleWidthsV2} data-testid="int-image-variants-widths-v2" />
      </div>
    </div>
  );
};

export default ImageVariantsCard;
